import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp, GripVertical, RotateCcw, X } from "lucide-react";
import {
  INTERNAL_BLOCKS,
  LOCKED_INTERNAL_BLOCK_ID,
  normalizeInternalBlockOrder,
} from "./v2OverviewConstants";

function moveBy(order, id, delta) {
  if (id === LOCKED_INTERNAL_BLOCK_ID) return order;
  const next = [...order];
  const index = next.indexOf(id);
  const target = index + delta;
  if (index < 1 || target < 1 || target >= next.length) return order;
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item);
  return next;
}

function moveTo(order, fromId, toId) {
  if (!fromId || fromId === LOCKED_INTERNAL_BLOCK_ID || toId === LOCKED_INTERNAL_BLOCK_ID) {
    return order;
  }
  const next = order.filter((id) => id !== fromId);
  const target = Math.max(1, next.indexOf(toId));
  if (target < 1) return normalizeInternalBlockOrder([...next, fromId]);
  next.splice(target, 0, fromId);
  return normalizeInternalBlockOrder(next);
}

export default function V2BlockOrderModal({
  open,
  order,
  defaultOrder,
  onSave,
  onReset,
  onClose,
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(order);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);

  useEffect(() => {
    if (open) {
      setDraft(normalizeInternalBlockOrder(order));
      setDragId(null);
      setOverId(null);
    }
  }, [open, order]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const blocks = useMemo(
    () => draft.map((id) => INTERNAL_BLOCKS.find((block) => block.id === id)).filter(Boolean),
    [draft],
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] grid place-items-center bg-[rgba(4,2,22,0.72)] px-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-[460px] rounded-2xl border border-[rgba(98,94,166,0.45)] bg-[#14104a] p-5 shadow-[0_24px_60px_rgba(2,0,16,0.55)]"
        role="dialog"
        aria-labelledby="v2-block-order-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2
              id="v2-block-order-title"
              className="m-0 font-display text-[22px] font-bold uppercase tracking-[0.04em] text-[#f4f2ff]"
            >
              {t("v2.overview.block_order")}
            </h2>
            <p className="mt-1.5 text-[12px] leading-5 text-[#9b96c9]">
              {t("v2.overview.block_order_help")}
            </p>
          </div>
          <button
            type="button"
            className="grid h-8 w-8 place-items-center rounded-full border-0 bg-transparent text-[#9b96c9] hover:bg-[rgba(98,94,166,0.2)] hover:text-white"
            onClick={onClose}
            aria-label={t("v2.overview.block_order_close")}
          >
            <X size={16} />
          </button>
        </header>

        <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.08em] text-[#8b86bc]">
          {t("v2.overview.block_order_scope")}
        </p>

        <ol className="m-0 flex list-none flex-col gap-2 p-0">
          {blocks.map((block, index) => {
            const locked = block.id === LOCKED_INTERNAL_BLOCK_ID;
            const isOver = overId === block.id && dragId && dragId !== block.id && !locked;
            return (
              <li
                key={block.id}
                className={`grid min-h-[46px] grid-cols-[18px_22px_1fr_auto] items-center gap-2 rounded-[10px] border px-3 ${
                  isOver
                    ? "border-[#34cbff] bg-[rgba(52,203,255,0.08)]"
                    : "border-[rgba(98,94,166,0.45)] bg-[rgba(17,12,61,0.72)]"
                } ${locked ? "opacity-80" : ""} ${dragId === block.id ? "opacity-55" : ""}`}
                draggable={!locked}
                onDragStart={(event) => {
                  if (locked) {
                    event.preventDefault();
                    return;
                  }
                  setDragId(block.id);
                  event.dataTransfer.effectAllowed = "move";
                }}
                onDragOver={(event) => {
                  if (locked || !dragId) return;
                  event.preventDefault();
                  setOverId(block.id);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  setDraft((current) => moveTo(current, dragId, block.id));
                  setDragId(null);
                  setOverId(null);
                }}
                onDragEnd={() => {
                  setDragId(null);
                  setOverId(null);
                }}
              >
                <GripVertical
                  size={16}
                  className={locked ? "text-[#5a5698]" : "cursor-grab text-[#8b86bc]"}
                />
                <span className="text-[12px] font-semibold text-[#9b96c9]">{index + 1}</span>
                <strong className="truncate text-[13px] font-semibold text-[#f4f2ff]">
                  {t(block.titleKey)}
                </strong>
                <span className="flex flex-col">
                  <button
                    type="button"
                    className="grid h-4 w-5 place-items-center border-0 bg-transparent p-0 text-[#c9c5f5] disabled:text-[#5a5698]"
                    disabled={locked || index <= 1}
                    onClick={() => setDraft((current) => moveBy(current, block.id, -1))}
                    aria-label={t("v2.overview.block_order_up", { name: t(block.titleKey) })}
                  >
                    <ChevronUp size={14} />
                  </button>
                  <button
                    type="button"
                    className="grid h-4 w-5 place-items-center border-0 bg-transparent p-0 text-[#c9c5f5] disabled:text-[#5a5698]"
                    disabled={locked || index === blocks.length - 1}
                    onClick={() => setDraft((current) => moveBy(current, block.id, 1))}
                    aria-label={t("v2.overview.block_order_down", { name: t(block.titleKey) })}
                  >
                    <ChevronDown size={14} />
                  </button>
                </span>
              </li>
            );
          })}
        </ol>

        <footer className="mt-5 flex items-center justify-between gap-3">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 border-0 bg-transparent p-0 text-[12px] text-[#9b96c9] hover:text-white"
            onClick={() => setDraft(defaultOrder)}
          >
            <RotateCcw size={13} />
            {t("v2.overview.block_order_reset")}
          </button>
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="border-0 bg-transparent p-0 text-[13px] text-[#c9c5f5] hover:text-white"
              onClick={onClose}
            >
              {t("v2.overview.block_order_cancel")}
            </button>
            <button
              type="button"
              className="min-h-9 rounded-lg bg-[#064ff5] px-4 text-[13px] font-semibold text-white hover:bg-[#1f48ff]"
              onClick={() => {
                onSave(draft);
                onClose();
              }}
            >
              {t("v2.overview.block_order_save")}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
